import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, Check } from "lucide-react";

import { useT, LANGUAGES } from "../i18n";
import { SERVICES } from "../content/services";
import { Wordmark, Button } from "./ui/primitives";

// Two tiers, as in the design: a gradient hairline and brand row on top, the
// full seven-service nav underneath.
//
// Seven items at 13px fit at 1440 and nowhere near it. Below xl the second
// tier scrolls horizontally rather than wrapping into a second line that
// pushes the page down; below lg the whole thing collapses into a panel.

function LanguageMenu() {
  const { lang, setLang, t } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 font-mono text-[12px] tracking-[0.6px] text-fg-secondary
                   transition-colors duration-200 hover:text-fg"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("nav.language")}
      >
        <span>{lang.toUpperCase()}</span>
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          className={`text-fg-muted transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <ul
          className="animate-fade-in absolute right-0 top-full z-50 mt-3 w-[150px] overflow-hidden
                     rounded-lg border border-line bg-surface-raised py-1 shadow-console"
          role="listbox"
        >
          {LANGUAGES.map((l) => (
            <li key={l.code}>
              <button
                type="button"
                role="option"
                aria-selected={l.code === lang}
                onClick={() => {
                  setLang(l.code);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between gap-2 px-3.5 py-2 text-left
                           font-body text-[13.5px] text-fg-secondary transition-colors duration-150
                           hover:bg-chip-neutral hover:text-fg"
              >
                <span>{l.nativeLabel}</span>
                {l.code === lang && <Check size={13} strokeWidth={2.4} className="text-accent" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Header() {
  const { t, lang } = useT();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  // Which side of the service nav still has content off screen. Drives the
  // fade overlays; recalculated on scroll, on resize and when the language
  // changes, since label widths change with it.
  const navRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const updateEdges = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdges({ left: el.scrollLeft > 4, right: max > 4 && el.scrollLeft < max - 4 });
  }, []);

  useEffect(() => {
    updateEdges();
    window.addEventListener("resize", updateEdges);
    return () => window.removeEventListener("resize", updateEdges);
  }, [updateEdges, lang]);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-40 bg-surface">
      {/* The one gradient on the site. Two pixels, edge to edge. */}
      <div className="h-0.5 w-full bg-gradient-to-r from-accent to-accent-2" aria-hidden="true" />

      <div className="border-b border-line-subtle">
        <div className="shell flex h-[68px] items-center justify-between gap-6 lg:h-[76px]">
          <Wordmark to="/" />

          <div className="flex items-center gap-4 sm:gap-6">
            <div className="hidden sm:block">
              <LanguageMenu />
            </div>
            <span className="hidden h-4 w-px bg-line sm:block" aria-hidden="true" />
            <Button to="/contact" variant="light" size="sm" arrow={false} className="hidden sm:inline-flex">
              {t("nav.contact")}
            </Button>

            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="-mr-2 p-2 text-fg lg:hidden"
              aria-expanded={open}
              aria-label={open ? t("nav.close") : t("nav.menu")}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Service tier. Desktop only.
          Seven labels fit comfortably in English at 1440. In German and French
          they do not fit on a 1024 laptop, so the row scrolls and gets a fade
          on the edge that still has content, rather than cutting an item off
          with no hint that more exists. */}
      <nav aria-label={t("nav.services")} className="hidden border-b border-line-subtle lg:block">
        <div className="relative">
          <div
            ref={navRef}
            onScroll={updateEdges}
            className="shell no-scrollbar flex items-center gap-4 overflow-x-auto wide:gap-[26px]"
          >
            {SERVICES.map((s) => (
              <NavLink
                key={s.slug}
                to={`/${s.slug}`}
                className={({ isActive }) =>
                  `shrink-0 whitespace-nowrap border-b-2 pb-[13px] pt-[15px] font-body text-[13px]
                   transition-colors duration-200 ${
                     isActive
                       ? "border-accent text-fg"
                       : "border-transparent text-fg-secondary hover:text-fg"
                   }`
                }
              >
                {t(`services.${s.slug}.navLabel`)}
              </NavLink>
            ))}
          </div>

          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r
                        from-surface to-transparent transition-opacity duration-200
                        ${edges.left ? "opacity-100" : "opacity-0"}`}
          />
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l
                        from-surface to-transparent transition-opacity duration-200
                        ${edges.right ? "opacity-100" : "opacity-0"}`}
          />
        </div>
      </nav>

      {/* Mobile panel */}
      {open && (
        <div className="animate-fade-in border-b border-line-subtle bg-surface lg:hidden">
          <nav className="shell flex max-h-[calc(100vh-70px)] flex-col gap-1 overflow-y-auto py-6">
            <span className="pb-2 font-caption text-[11px] uppercase tracking-[1.6px] text-fg-muted">
              {t("nav.services")}
            </span>
            {SERVICES.map((s, i) => (
              <NavLink
                key={s.slug}
                to={`/${s.slug}`}
                className={({ isActive }) =>
                  `flex items-center gap-4 border-t border-line-subtle py-3.5 font-body text-[16px]
                   transition-colors duration-200 ${isActive ? "text-accent" : "text-fg"}`
                }
              >
                <span className="font-mono text-[12px] text-fg-muted tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {t(`services.${s.slug}.navLabel`)}
              </NavLink>
            ))}
            <div className="mt-5 flex items-center justify-between gap-4">
              <Button to="/contact" variant="light" size="sm" arrow={false}>
                {t("nav.contact")}
              </Button>
              <LanguageMenu />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
